import { TrackedEvents, trackEvent } from 'analytics';
import type { Country, FullDemandSignalDTO } from 'api';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActionRow, Box, Button, CircularProgress, Grid, Pagination, Section, Typography } from 'ui';
import { Discount } from 'ui/icons';

import { PAGE_SIZE } from '../../constants';
import { useMatchedMaterialsWithOffers } from '../../hooks/useMatchedMaterialsWithOffers/useMatchedMaterialsWithOffers';
import { DemandSignalForm, type TradingDashboardFormValuesType } from '../DemandSignalForm/DemandSignalForm';
import { MatchedSiteMaterialsList } from '../MatchedSiteMaterialsList/MatchedSiteMaterialsList';

export const MaterialMatcher = () => {
  const [page, setPage] = useState(1);

  const [selectedDemandSignals, setSelectedDemandSignals] = useState<FullDemandSignalDTO[]>([]);
  const [accountManagerIds, setAccountManagersIds] = useState<number[] | undefined>(undefined);
  const [country, setCountry] = useState<Country | undefined>(undefined);
  const [excludeCountry, setExcludeCountry] = useState<Country | undefined>(undefined);
  const [region, setRegion] = useState<string | undefined>(undefined);
  const [sellerId, setSellerId] = useState<number | undefined>(undefined);
  const [targetMargin, setTargetMargin] = useState<number | undefined>(undefined);
  const [minGrade, setMinGrade] = useState<number | undefined>(undefined);
  const [maxGrade, setMaxGrade] = useState<number | undefined>(undefined);

  const {
    allOffers,
    matchedMaterials,
    isLoading: isLoadingOffers,
    isFetched: isOffersFetched,
  } = useMatchedMaterialsWithOffers({
    accountManagerIds,
    country,
    demandSignals: selectedDemandSignals,
    excludeCountry,
    maxGrade,
    minGrade,
    page,
    pageSize: PAGE_SIZE,
    region: region ? Number(region) : undefined,
    sellerId,
    targetMargin,
  });

  const totalPages = useMemo(() => {
    if (matchedMaterials?.count) {
      return Math.ceil(matchedMaterials.count / PAGE_SIZE);
    }
    return 0;
  }, [matchedMaterials?.count]);

  const onFiltersSubmit = (values: TradingDashboardFormValuesType) => {
    setPage(1); // Reset to first page on new search
    setAccountManagersIds(values.account_managers ? values.account_managers.map((am) => am.id) : []);
    setCountry(values.site_material_country ?? undefined);
    setExcludeCountry(values.exclude_country ?? undefined);
    setRegion(values.site_material_region ?? undefined);
    setSelectedDemandSignals(values.demand_signals as FullDemandSignalDTO[]);
    setTargetMargin(values.target_margin);
    setSellerId(values.seller_id ?? undefined);
    setMinGrade(values.min_grade ?? undefined);
    setMaxGrade(values.max_grade ?? undefined);
    trackEvent(TrackedEvents.TRADING_DASHBOARD_SEARCH, {
      filters: values,
    });
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Section
          title="Filters"
          icon={Discount}
          action={
            <ActionRow>
              <Button variant="text" component={Link} to="/ground-control/trade-signals/demand/create">
                Create demand signal
              </Button>
              <Button variant="text" component={Link} to="/ground-control/trade-signals/supply/create">
                Create supply signal
              </Button>
            </ActionRow>
          }
        >
          <DemandSignalForm onFiltersSubmit={onFiltersSubmit} isLoading={isLoadingOffers} />
        </Section>
      </Grid>
      <Grid item xs={12}>
        <Box mb={2}>
          <Typography variant="h5" fontWeight="bold">
            Demand and supply matches
          </Typography>
        </Box>

        {isLoadingOffers ? (
          <Box display="flex" justifyContent="center" alignItems="center" my={2}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isOffersFetched ? <MatchedSiteMaterialsList materials={allOffers} targetMargin={targetMargin} /> : null}
            {allOffers.length === 0 && !isOffersFetched ? (
              <Box mt={2}>
                <Typography textAlign="center">No materials found matching your criteria</Typography>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" mt={3} mb={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePaginationChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};
